import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['accordion', 'show', 'hide', 'reviewModal', 'btnArea', 'review', 'reviewsOpen', 'reviewsClose']
  static values = {
    limit: { type: Number, default: 5 },
    reviewId: String,
  }

  className () {
    return this.element.dataset.classname
  }

  initialize () {
    this.currentReviewImages = []
    this.currentImageIndex = 0
  }

  connect () {
    const hiddenClass = `${this.className()}::hidden`

    // アコーディオン全件個別判定
    this.accordionTargets.forEach((accordion, idx) => {
      const show = this.showTargets[idx]
      if (!show) return
      if (accordion.clientHeight > accordion.dataset.height) {
        show.style.display = 'flex'
        accordion.classList.add(hiddenClass)
      } else {
        show.style.display = 'none'
        accordion.classList.remove(hiddenClass)
        this.btnAreaTargets[idx].classList.add(`${this.className()}::button-rest-area--open`)
      }
    })

    // limit を超えるレビューは非表示
    this.reviewTargets.forEach((review, i) => {
      if (i >= this.limitValue) {
        review.classList.add(hiddenClass)
      } else {
        review.classList.remove(hiddenClass)
      }
    })

    // review_idパラメータがある場合、該当口コミにスクロールして開く
    if (this.hasReviewIdValue) {
      // DOM読み込み完了後に実行
      setTimeout(() => {
        this.scrollToAndOpenReview(this.reviewIdValue)
      }, 300)
    }
  }

  scrollToAndOpenReview (reviewId) {
    const reviewElement = document.getElementById(`review${reviewId}`)
    if (!reviewElement) return

    // 該当口コミまでスクロール
    reviewElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

    // review要素内からshowボタンを直接探す
    const showButton = reviewElement.querySelector('[data-site--parts--facility--review-list--desktop--component-target="show"]')
    if (showButton && showButton.style.display !== 'none') {
      // スクロール完了後にボタンをクリック
      setTimeout(() => {
        showButton.click()
      }, 500)
    }
  }

  show (e) {
    // e.currentTargetを使用してイベントリスナーが登録された要素を取得
    // e.targetは子要素（アイコン等）になる可能性があるため
    const button = e.currentTarget
    const idx = this.showTargets.indexOf(button)
    if (idx === -1) return

    const accordion = this.accordionTargets[idx]
    const hide = this.hideTargets[idx]
    if (!accordion || !hide) return

    accordion.classList.remove(`${this.className()}::hidden`)
    hide.style.display = 'flex'
    button.style.display = 'none'
    this.btnAreaTargets[idx].classList.add(`${this.className()}::button-rest-area--open`)
  }

  hide (e) {
    // e.currentTargetを使用してイベントリスナーが登録された要素を取得
    const button = e.currentTarget
    const idx = this.hideTargets.indexOf(button)
    if (idx === -1) return

    const accordion = this.accordionTargets[idx]
    const show = this.showTargets[idx]
    if (!accordion || !show) return

    accordion.classList.add(`${this.className()}::hidden`)
    show.style.display = 'flex'
    button.style.display = 'none'
    this.btnAreaTargets[idx].classList.remove(`${this.className()}::button-rest-area--open`)
  }

  reviewsOpen () {
    const hiddenClass = `${this.className()}::hidden`
    this.reviewTargets.forEach((review, index) => {
      if (index >= this.limitValue) {
        review.classList.remove(hiddenClass)
      }
    })
    this.reviewsOpenTarget.style.display = 'none'
    this.reviewsCloseTarget.style.display = 'flex'
  }

  reviewsClose () {
    const hiddenClass = `${this.className()}::hidden`
    this.reviewTargets.forEach((review, index) => {
      if (index >= this.limitValue) {
        review.classList.add(hiddenClass)
      }
    })
    this.reviewsOpenTarget.style.display = 'flex'
    this.reviewsCloseTarget.style.display = 'none'
  }

  showReviewImageModal (e) {
    e.preventDefault()

    this.clearModalContent()

    const reviewContainer = e.currentTarget.closest('[id^="review"]')
    const imageItems = reviewContainer.querySelectorAll('[data-action*="showReviewImageModal"]')

    this.currentReviewImages = Array.from(imageItems).map(item => ({
      largeImageUrl: item.dataset.largeImageUrl,
      alt: item.querySelector('img').alt,
      categoryName: item.dataset.categoryName,
      note: item.dataset.note,
    }))

    this.currentImageIndex = parseInt(e.currentTarget.dataset.imageIndex)

    this.updateModalContent()
    this.reviewModalTarget.classList.add(`${this.className()}::visible`)
  }

  hideReviewModal (e) {
    if (e) e.preventDefault()
    this.reviewModalTarget.classList.remove(`${this.className()}::visible`)
    document.body.style.overflow = ''
  }

  clearModalContent () {
    const modal = this.reviewModalTarget
    const modalImage = modal.querySelector('img')
    const modalInfo = modal.querySelector('div[class*="info"]')
    const prevBtn = modal.querySelector('[data-action*="prevImage"]')
    const nextBtn = modal.querySelector('[data-action*="nextImage"]')

    if (modalImage) {
      modalImage.src = ''
      modalImage.alt = ''
    }

    if (modalInfo) {
      modalInfo.style.display = 'none'
      modalInfo.textContent = ''
    }

    if (prevBtn) prevBtn.style.display = 'none'
    if (nextBtn) nextBtn.style.display = 'none'
  }

  prevImage () {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--
      this.updateModalContent()
    }
  }

  nextImage () {
    if (this.currentImageIndex < this.currentReviewImages.length - 1) {
      this.currentImageIndex++
      this.updateModalContent()
    }
  }

  updateModalContent () {
    const modal = this.reviewModalTarget
    const modalImage = modal.querySelector('img')
    const modalInfo = modal.querySelector('div[class*="info"]')
    const prevBtn = modal.querySelector('[data-action*="prevImage"]')
    const nextBtn = modal.querySelector('[data-action*="nextImage"]')

    const imageData = this.currentReviewImages[this.currentImageIndex]
    if (modalImage && imageData) {
      modalImage.src = imageData.largeImageUrl
      modalImage.alt = imageData.alt
    }

    if (modalInfo) {
      if (imageData.note && imageData.note.trim() !== '') {
        modalInfo.textContent = imageData.note
        modalInfo.style.display = 'block'
      } else {
        modalInfo.style.display = 'none'
      }
    }

    if (this.currentReviewImages.length <= 1) {
      if (prevBtn) prevBtn.style.display = 'none'
      if (nextBtn) nextBtn.style.display = 'none'
    } else {
      if (prevBtn) {
        if (this.currentImageIndex === 0) {
          prevBtn.style.display = 'none'
        } else {
          prevBtn.style.display = 'block'
        }
      }
      if (nextBtn) {
        if (this.currentImageIndex === this.currentReviewImages.length - 1) {
          nextBtn.style.display = 'none'
        } else {
          nextBtn.style.display = 'block'
        }
      }
    }
  }
}
